/**
 * Activation funnel for Sankey: sign-up → onboarding → (profile | workspace) → scan → upload → orders.
 * Profile is an optional branch from onboarding; main path continues via workspace.
 * Started-scan counts union `scan_started` events, Firestore `scans` docs, user-created workspaces (scan gate),
 * and Practice Space scan signals (`practice_workspace_opened`, `post_onboarding_start_scanning_tapped`).
 */

import { getContactProfileDetailRows } from "./contactProfileFields";
import { contactCreatedAtMs } from "./db";
import { filterByTimestamp } from "./dateRangeFilter";
import {
  isUserCreatedWorkspace,
  userCreatedWorkspaceIdSet,
  defaultPracticeWorkspaceIdSet,
  workspaceIdFromDoc,
} from "./workspaceClassification";
import { PRACTICE_SPACE_SCAN_SIGNAL_EVENTS } from "./productAnalyticsEvents";
import { productDocUserRef, paidOrdersByUser, fmtDate, tableRowsToCSV } from "./productAnalyticsMetrics";

/** Activation Sankey stage keys that support drill-down + CSV export. */
export const ACTIVATION_SANKEY_EXPORT_KEYS = new Set([
  "signups",
  "onboarding",
  "profile",
  "workspace",
  "started_scan",
  "upload",
  "first_order",
  "second_order",
]);

const STAGE_LABELS = {
  signups: "Total sign-ups",
  onboarding: "Onboarding details",
  profile: "Profile section",
  workspace: "New workspace (client or practice)",
  started_scan: "Started scanning",
  upload: "First file upload",
  first_order: "First order",
  second_order: "2nd order",
};

function normalizeListValue(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v).trim()).filter(Boolean);
  const s = String(raw).trim();
  if (!s) return [];
  if (s.includes(",")) return s.split(",").map((v) => v.trim()).filter(Boolean);
  return [s];
}

/** Onboarding form filled (profession + at least one more onboarding field). */
export function hasOnboardingDetails(contact) {
  if (!contact) return false;
  const hasProfession = Boolean(contact.workRole && String(contact.workRole).trim());
  const hasCompany = Boolean(contact.companyName && String(contact.companyName).trim());
  const hasProject = normalizeListValue(contact.projectTypes).length > 0;
  const hasFormat = Boolean(contact.preferredFormat && String(contact.preferredFormat).trim());
  return hasProfession && (hasCompany || hasProject || hasFormat);
}

/** Profile section has at least one field saved. */
export function hasProfileSection(contact) {
  if (!contact) return false;
  const { profile } = getContactProfileDetailRows(contact);
  return profile.length > 0;
}

function contactUserKeys(contact) {
  const set = new Set();
  const id = contact?.id != null ? String(contact.id).trim() : "";
  const uid = contact?.firebaseUid != null ? String(contact.firebaseUid).trim() : "";
  const email = contact?.email != null ? String(contact.email).trim().toLowerCase() : "";
  if (id) set.add(id);
  if (uid) set.add(uid);
  if (email && email.includes("@")) set.add(email);
  return set;
}

function memberUidFromDocId(docId) {
  if (!docId || typeof docId !== "string") return null;
  const re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_([A-Za-z0-9]{20,36})$/i;
  const m = docId.trim().match(re);
  return m ? m[1] : null;
}

function workspaceOwnerRef(ws) {
  const owner = ws?.owner_user_id ?? ws?.ownerUserId;
  if (owner != null && String(owner).trim()) return String(owner).trim();
  return productDocUserRef(ws);
}

/** Users with a user-created client or practice workspace (owner or member). Excludes auto Practice Space only. */
function collectUserCreatedWorkspaceUserRefs(workspaces = [], workspaceMembers = []) {
  const countableIds = userCreatedWorkspaceIdSet(workspaces);
  const refs = new Set();

  for (const ws of workspaces) {
    if (!isUserCreatedWorkspace(ws)) continue;
    const u = workspaceOwnerRef(ws);
    if (u) refs.add(u);
  }

  for (const wm of workspaceMembers) {
    const wid = workspaceIdFromDoc(wm);
    if (!wid || !countableIds.has(wid)) continue;
    const u = productDocUserRef(wm) || memberUidFromDocId(wm.id);
    if (u) refs.add(u);
  }

  return refs;
}

function collectFileUserRefs(files = []) {
  const refs = new Set();
  for (const f of files) {
    const u = productDocUserRef(f);
    if (u) refs.add(u);
  }
  return refs;
}

function scanDocWorkspaceId(scan) {
  const id =
    scan?.workspace_id ??
    scan?.workspaceId ??
    scan?.workspaceID ??
    null;
  return id != null && String(id).trim() ? String(id).trim() : "";
}

const PRACTICE_SCAN_SIGNAL_SET = new Set(
  PRACTICE_SPACE_SCAN_SIGNAL_EVENTS.map((e) => String(e).toLowerCase())
);
const SCAN_STARTED_EVENT = "scan_started";

/** Users with scan_started, Practice Space signals, Firestore scans, or workspace scan-gate evidence. */
function collectScanStartedUserRefs(productAnalyticsEvents = [], scans = [], workspaces = []) {
  const refs = new Set();
  const practiceWsIds = defaultPracticeWorkspaceIdSet(workspaces);

  for (const ev of productAnalyticsEvents) {
    const name = String(ev?.event_name ?? ev?.eventName ?? "").toLowerCase();
    if (name !== SCAN_STARTED_EVENT && !PRACTICE_SCAN_SIGNAL_SET.has(name)) continue;
    const u = productDocUserRef(ev);
    if (u) refs.add(u);
  }

  for (const s of scans) {
    const u = productDocUserRef(s);
    if (u) refs.add(u);
    const wid = scanDocWorkspaceId(s);
    if (wid && practiceWsIds.has(wid) && u) refs.add(u);
  }

  for (const ws of workspaces) {
    if (!isUserCreatedWorkspace(ws)) continue;
    const u = workspaceOwnerRef(ws);
    if (u) refs.add(u);
  }

  return refs;
}

function contactMatchesRefSet(contact, refSet) {
  if (!refSet?.size) return false;
  for (const k of contactUserKeys(contact)) {
    if (refSet.has(k)) return true;
  }
  return false;
}

function paidOrderCountForContact(contact, ordersByUid) {
  let max = 0;
  for (const k of contactUserKeys(contact)) {
    const n = ordersByUid.get(k)?.length ?? 0;
    if (n > max) max = n;
  }
  return max;
}

function hasWorkspace(contact, workspaceUsers) {
  return contactMatchesRefSet(contact, workspaceUsers);
}

function hasUpload(contact, fileUsers) {
  return contactMatchesRefSet(contact, fileUsers);
}

function hasStartedScan(contact, scanStartedUsers) {
  return contactMatchesRefSet(contact, scanStartedUsers);
}

function buildLink(source, target, value, fromValue, extra = {}) {
  const dropped = Math.max(0, fromValue - value);
  return {
    source,
    target,
    value,
    dropped,
    dropPct: fromValue > 0 ? (dropped / fromValue) * 100 : null,
    stepConvPct: fromValue > 0 ? (value / fromValue) * 100 : null,
    ...extra,
  };
}

function signupsInRange(contacts = [], dateRange) {
  const pool = contacts.filter((c) => !c.isInternal);
  return filterByTimestamp(pool, contactCreatedAtMs, dateRange);
}

/**
 * @returns {{ nodes: Array, links: Array, signupsTotal: number }}
 */
export function computeActivationSankey(
  contacts = [],
  workspaces = [],
  workspaceMembers = [],
  files = [],
  orders = [],
  scans = [],
  productAnalyticsEvents = [],
  dateRange = null
) {
  const signups = signupsInRange(contacts, dateRange);
  const workspaceUsers = collectUserCreatedWorkspaceUserRefs(workspaces, workspaceMembers);
  const fileUsers = collectFileUserRefs(files);
  const scanStartedUsers = collectScanStartedUserRefs(productAnalyticsEvents, scans, workspaces);
  const { map: ordersByUid } = paidOrdersByUser(Array.isArray(orders) ? orders : []);

  const counts = {
    signups: signups.length,
    onboarding: signups.filter(hasOnboardingDetails).length,
    profile: signups.filter((c) => hasOnboardingDetails(c) && hasProfileSection(c)).length,
    workspace: signups.filter((c) => hasWorkspace(c, workspaceUsers)).length,
    onboardingWorkspace: signups.filter((c) => hasOnboardingDetails(c) && hasWorkspace(c, workspaceUsers)).length,
    started_scan: signups.filter((c) => hasStartedScan(c, scanStartedUsers)).length,
    workspaceStartedScan: signups.filter(
      (c) => hasWorkspace(c, workspaceUsers) && hasStartedScan(c, scanStartedUsers)
    ).length,
    upload: signups.filter((c) => hasUpload(c, fileUsers)).length,
    startedScanUpload: signups.filter(
      (c) => hasStartedScan(c, scanStartedUsers) && hasUpload(c, fileUsers)
    ).length,
    first_order: signups.filter((c) => paidOrderCountForContact(c, ordersByUid) >= 1).length,
    second_order: signups.filter((c) => paidOrderCountForContact(c, ordersByUid) >= 2).length,
  };

  const total = counts.signups;
  const pct = (n) => (total > 0 ? (n / total) * 100 : null);

  const nodes = [
    { id: "signups", key: "signups", label: "Total sign-ups", shortLabel: "Sign-ups", value: counts.signups, column: 0, row: 0.5, color: "var(--chart-line)", pctOfSignups: pct(counts.signups) },
    { id: "onboarding", key: "onboarding", label: "Onboarding details", shortLabel: "Onboarding", value: counts.onboarding, column: 1, row: 0.5, color: "var(--info-500)", pctOfSignups: pct(counts.onboarding) },
    { id: "profile", key: "profile", label: "Profile section", shortLabel: "Profile", value: counts.profile, column: 2, row: 0.2, color: "var(--purple)", optional: true, pctOfSignups: pct(counts.profile) },
    {
      id: "workspace",
      key: "workspace",
      label: "New workspace",
      shortLabel: "Client + practice",
      value: counts.workspace,
      column: 3,
      row: 0.5,
      color: "var(--secondary-500)",
      pctOfSignups: pct(counts.workspace),
    },
    {
      id: "started_scan",
      key: "started_scan",
      label: "Started scanning",
      shortLabel: "Started scan",
      value: counts.started_scan,
      column: 4,
      row: 0.5,
      color: "var(--purple)",
      pctOfSignups: pct(counts.started_scan),
    },
    { id: "upload", key: "upload", label: "First file upload", shortLabel: "Upload", value: counts.upload, column: 5, row: 0.5, color: "var(--accent)", pctOfSignups: pct(counts.upload) },
    { id: "first_order", key: "first_order", label: "First order", shortLabel: "1st order", value: counts.first_order, column: 6, row: 0.5, color: "var(--green)", pctOfSignups: pct(counts.first_order) },
    { id: "second_order", key: "second_order", label: "2nd order", shortLabel: "2nd order", value: counts.second_order, column: 7, row: 0.5, color: "var(--success-500)", pctOfSignups: pct(counts.second_order) },
  ];

  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]));

  const links = [
    buildLink("signups", "onboarding", counts.onboarding, counts.signups, {
      fromLabel: nodeById.signups.label,
      toLabel: nodeById.onboarding.label,
    }),
    {
      source: "onboarding",
      target: "profile",
      value: counts.profile,
      dropped: 0,
      dropPct: null,
      stepConvPct: counts.onboarding > 0 ? (counts.profile / counts.onboarding) * 100 : null,
      optional: true,
      fromLabel: nodeById.onboarding.label,
      toLabel: nodeById.profile.label,
    },
    buildLink("onboarding", "workspace", counts.onboardingWorkspace, counts.onboarding, {
      fromLabel: nodeById.onboarding.label,
      toLabel: nodeById.workspace.label,
      mainPath: true,
    }),
    buildLink("workspace", "started_scan", counts.workspaceStartedScan, counts.workspace, {
      fromLabel: nodeById.workspace.label,
      toLabel: nodeById.started_scan.label,
      mainPath: true,
    }),
    buildLink("started_scan", "upload", counts.startedScanUpload, counts.started_scan, {
      fromLabel: nodeById.started_scan.label,
      toLabel: nodeById.upload.label,
      mainPath: true,
    }),
    buildLink("upload", "first_order", counts.first_order, counts.upload, {
      fromLabel: nodeById.upload.label,
      toLabel: nodeById.first_order.label,
      mainPath: true,
    }),
    buildLink("first_order", "second_order", counts.second_order, counts.first_order, {
      fromLabel: nodeById.first_order.label,
      toLabel: nodeById.second_order.label,
      mainPath: true,
    }),
  ];

  return {
    nodes,
    links,
    stages: nodes,
    signupsTotal: signups.length,
    computedAt: Date.now(),
  };
}

function buildActivationContext(
  contacts = [],
  workspaces = [],
  workspaceMembers = [],
  files = [],
  orders = [],
  scans = [],
  productAnalyticsEvents = [],
  dateRange = null
) {
  const signups = signupsInRange(contacts, dateRange);
  const workspaceUsers = collectUserCreatedWorkspaceUserRefs(workspaces, workspaceMembers);
  const fileUsers = collectFileUserRefs(files);
  const scanStartedUsers = collectScanStartedUserRefs(productAnalyticsEvents, scans, workspaces);
  const { map: ordersByUid } = paidOrdersByUser(Array.isArray(orders) ? orders : []);
  return { signups, workspaceUsers, fileUsers, scanStartedUsers, ordersByUid };
}

/** Contacts that reached a given activation Sankey stage. */
export function contactsAtActivationStage(
  stageKey,
  contacts = [],
  workspaces = [],
  workspaceMembers = [],
  files = [],
  orders = [],
  scans = [],
  productAnalyticsEvents = [],
  dateRange = null
) {
  const { signups, workspaceUsers, fileUsers, scanStartedUsers, ordersByUid } = buildActivationContext(
    contacts,
    workspaces,
    workspaceMembers,
    files,
    orders,
    scans,
    productAnalyticsEvents,
    dateRange
  );

  switch (stageKey) {
    case "signups":
      return signups;
    case "onboarding":
      return signups.filter(hasOnboardingDetails);
    case "profile":
      return signups.filter((c) => hasOnboardingDetails(c) && hasProfileSection(c));
    case "workspace":
      return signups.filter((c) => hasWorkspace(c, workspaceUsers));
    case "started_scan":
      return signups.filter((c) => hasStartedScan(c, scanStartedUsers));
    case "upload":
      return signups.filter((c) => hasUpload(c, fileUsers));
    case "first_order":
      return signups.filter((c) => paidOrderCountForContact(c, ordersByUid) >= 1);
    case "second_order":
      return signups.filter((c) => paidOrderCountForContact(c, ordersByUid) >= 2);
    default:
      return [];
  }
}

/** Drill-modal table for an activation Sankey stage. */
export function activationSankeyDrillContent(
  stageKey,
  contacts = [],
  workspaces = [],
  workspaceMembers = [],
  files = [],
  orders = [],
  scans = [],
  productAnalyticsEvents = [],
  dateRange = null
) {
  if (!ACTIVATION_SANKEY_EXPORT_KEYS.has(stageKey)) return null;

  const stageContacts = contactsAtActivationStage(
    stageKey,
    contacts,
    workspaces,
    workspaceMembers,
    files,
    orders,
    scans,
    productAnalyticsEvents,
    dateRange
  );
  const label = STAGE_LABELS[stageKey] || stageKey;

  return {
    subtitle: `${stageContacts.length} user(s) reached “${label}”. Export CSV for email campaigns.`,
    headers: ["Name", "Email", "Company", "CRM stage", "Signed up"],
    rows: stageContacts.map((c) => ({
      k: `act-${stageKey}-${c.id}`,
      cells: [
        c.name || "—",
        c.email || "—",
        c.companyName || "—",
        c.stage || "—",
        contactCreatedAtMs(c) > 0 ? fmtDate(contactCreatedAtMs(c)) : "—",
      ],
      contact: c,
    })),
    showContactActions: true,
    csvExportable: true,
  };
}

export function activationSankeyCsvFilename(stageKey) {
  const slug = String(stageKey).replace(/_/g, "-");
  return `scanbrix-activation-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
}

export function downloadActivationSankeyCsv(
  stageKey,
  contacts = [],
  workspaces = [],
  workspaceMembers = [],
  files = [],
  orders = [],
  scans = [],
  productAnalyticsEvents = [],
  dateRange = null
) {
  if (!ACTIVATION_SANKEY_EXPORT_KEYS.has(stageKey)) return;
  const table = activationSankeyDrillContent(
    stageKey,
    contacts,
    workspaces,
    workspaceMembers,
    files,
    orders,
    scans,
    productAnalyticsEvents,
    dateRange
  );
  if (!table?.headers?.length) return;
  const csv = tableRowsToCSV(table.headers, table.rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = activationSankeyCsvFilename(stageKey);
  a.click();
  URL.revokeObjectURL(url);
}
